import React, {Component} from 'react';
import {specificApiConfig} from '../config';
import dayjs from 'dayjs'
import { getEasternTimeZoneAbbreviation } from '../globals';

export const UserContext = React.createContext();

const SAMPLE_TEST_PREFIX = "LFSAMPLE";

class UserContextProvider extends Component {
    constructor(props) {
        super(props)
        this.state = {
            isLoggedIn: false,
            userEmail: '',
            accessCode: '',
            originalAccessCode: '',
            testTimer: null, // a value of 0 or less means the test is timedout!
            deadline: '',
            deadlineDate: '',
            deadlineTimezoneAbbreviation: '',
            currentStep: '',
            currentTestStepNumber: null,
            clientUrlRoot: '',
            apiUrlRoot: '',
            leapFrogDashboardUrl : '',
            testUnderway: '',
            isLeapFrogTest: '', // Valid values include 'true' and ''. Used to check for Leapfrog tests (IF a site has a Leapfrog UNIQUE ID, then it is always a Leapfrog site)
            isSampleTest: '',
            sampleTestDeadline: '',
            sampleTestDeadlineDate: '',
            sampleTestDeadlineTimezoneAbbreviation: '',
            showTimeOutModal: false,
            testTimedOut: false,
            testContact: null,
            testFacilityName: null,
            testType: null,                 // "leapfrog" "ambulatory" (should be inpatient and track test/module type) from test template name
            leapFrogHospitalId: null,       //TODO: This needs refactoring, so the name is "Medicare Facility ID" with comments explaining LF calls it the LF ID
            ttGroup: null,                  // LF or VA from the name of the test template
            ttYear: null,                    // Year from test template name
            ttRole: null,                   // Test role from assignment table. Typically returned from survey client login. Valid values: null for never discovered; "" for no role; "<role display name>"
            // Global configuration: whether all timeouts are allowed/disallowed
            allTimeoutsAllowed: false,
            // EHR vendor code: "1" => VistA/CPRS (Legacy), "2" => Federal EHR (Cerner)
            ehrVendor: '',
        }
    }

    getApiConfig = ( apiName ) => {
        return this.state.apiUrlRoot + specificApiConfig[ apiName ];
    }

    isSampleAccessCode = (accessCode) => {
        return typeof accessCode === "string" && accessCode.startsWith(SAMPLE_TEST_PREFIX);
    }

    stripSamplePrefix = (accessCode) => {
        if (!this.isSampleAccessCode(accessCode)) {
            return accessCode;
        }

        return accessCode.substring(SAMPLE_TEST_PREFIX.length);
    }

    getRealAccessCode = () => {
        if (this.state.originalAccessCode && !this.isSampleAccessCode(this.state.originalAccessCode)) {
            return this.state.originalAccessCode;
        }

        return this.stripSamplePrefix(this.state.accessCode || "");
    }

    getSampleAccessCode = (accessCode) => {
        if (this.isSampleAccessCode(accessCode)) {
            return accessCode;
        }

        return `${SAMPLE_TEST_PREFIX}${accessCode || ""}`;
    }

    getEffectiveAccessCode = () => {
        if (this.state.isSampleTest) {
            return this.getSampleAccessCode(this.getRealAccessCode());
        }

        return this.state.accessCode;
    }

    loginUser = (user) => {
        this.setState({
            isLoggedIn: true,
            userEmail: user.email,
            accessCode: user.code,
        })
    }

    logoutUser = () => {
        this.setState({
            isLoggedIn: false,
            userEmail: '',
            accessCode: '',
            originalAccessCode: '',
            currentTestStepNumber: null,
            testTimer: null,
            deadline: '',
            deadlineDate: '',
            deadlineTimezoneAbbreviation: '',
            showTimeOutModal: false,
            testTimedOut: false,
            testUnderway: false,
            currentStep: '',
            previousStep: '',
            isLeapFrogTest: false,
            isSampleTest: false,
            sampleTestDeadline: '',
            sampleTestDeadlineDate: '',
            sampleTestDeadlineTimezoneAbbreviation: '',
            testContact: null,
            testFacilityName: null,
            testType: null,
            leapFrogHospitalId: null,
            ttGroup: null,
            ttYear: null,
            ttRole: null,
            ehrVendor: '',
            sessionTimer: 0
        })
    }


// New method to switch test (rewritten with a setState callback)
    switchTest = (newAccessCode) => {
        console.log("Access code switched to:", newAccessCode);

        // Clear localStorage and remove timeout
        window.localStorage.clear();

        // Use the setState callback so we know the state is updated
        this.setState({
            testTimedOut: false,
            currentStep: '',
            currentTestStepNumber: null,
            accessCode: newAccessCode,
            testTimer: null, // a value of 0 or less means the test is timed out
            sessionTimer: 0,
            previousStep: '',
            isSampleTest: false
        }, () => {
            // Now that currentStep is definitely '', we can safely fetch
            const apiConfig = this.getApiConfig("siteTestLogin");

            if (!apiConfig) {
                console.error("API config for 'siteTestLogin' not found.");
                return;
            }

            fetch(apiConfig, {
                method: "POST",
                mode: "cors",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json"
                },
                body: JSON.stringify({
                    "access-code": newAccessCode,
                    "email": this.state.userEmail // Ensure userEmail is defined in state
                }),
            })
                .then((response) => response.json())
                .then((data) => {
                    console.log('Login Data from handshake ->', data);
                    if (data.response && typeof data.response === 'string' && data.response.includes('SUCCESS')) {
                        this.setContactAndFacilityName(data);
                    } else {
                        console.log(`There was a problem switching tests.`);
                    }
                })
                .catch((error) => {
                    console.error("Error:", error);
                    console.log(`There was an error switching tests.`);
                });
        });
    };



    setCurrentStep = (step) => {
        this.setState({
            currentStep: step
        })
    }

    // This function is only called in the dashboard and in the useSetCurrentStep hook
    setStepNumber = (num) => {
        // console.log('Setting step to ', num);
        this.setState({
            currentTestStepNumber: num
        })
        if (num > 0 && num < 7) {
            this.setState({testUnderway: true})
            if (!this.state.isSampleTest) {
                this.checkAndSetStartTime(num)
            } // with the hook, we don't need to wait to check the time
        } else {
            this.setState({testUnderway: false})
        }
    }

    checkAndSetStartTime = (step) => {
        this.dayJSPrep()
        // console.log('fetching timeout status...');
        fetch(this.getApiConfig('getClockTimeoutStatus'),{
            method: "POST",
            mode: "cors",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json"
            },
            body: JSON.stringify({
                "access-code": this.state.accessCode,
            })
            })
            .then((response) => response.json())
            .then(data => {
                this.dayJSPrep()
                // console.log('clock status fetched:', data);
                let uTCOffsetPatient = dayjs(data['patient-section-start']).utc('z').local().format('M/D/YYYY h:mm:ss A')
                let uTCOffsetOrders = dayjs(data['orders-and-responses-start']).utc('z').local().format('M/D/YYYY h:mm:ss A')
                if (step <= 2) {this.timerStart(Date.parse(uTCOffsetPatient))}
                else {this.timerStart(Date.parse(uTCOffsetOrders))}
            })
            .catch(err => console.log('error checking timeout status', err))
    }

    dayJSPrep = () => {
        var utc = require('dayjs/plugin/utc')
        var timezone = require('dayjs/plugin/timezone')
        var customParseFormat = require('dayjs/plugin/customParseFormat')
        dayjs.extend(utc)
        dayjs.extend(customParseFormat)
        dayjs.extend(timezone)
    }

    calculateDeadlineDate = (time) => {
        this.dayJSPrep()
        let expirationString = new Date(time)
        let deadlineDate = dayjs(expirationString).tz("America/New_York").format('MM/DD/YYYY [at] hh:mm A')
        return deadlineDate
    }

    timerStart = (sectionStart) => {
        this.dayJSPrep()
        let threeHours = 3 * 60 * 60 * 1000
        // let threeHours = 1 * 60 * 1000 // for testing timeouts, dropping this to one minute
        let bonusFiveSeconds = 5 * 1000
        let testTimeLeft = sectionStart + threeHours + bonusFiveSeconds
        let deadlineDateString = this.calculateDeadlineDate(testTimeLeft);
        let deadlineString = deadlineDateString.substring(deadlineDateString.indexOf('t') + 2)
        this.setState({
            testTimer: testTimeLeft - Date.now(), // store remaining time
            deadline: deadlineString,
            deadlineDate: deadlineDateString,
            deadlineTimezoneAbbreviation: getEasternTimeZoneAbbreviation(testTimeLeft)
        })
    }


    getSampleDeadlineState = () => {
        this.dayJSPrep()
        // create a value for three hours from now
        let threeHoursFromNow = Date.now() + (3 * 60 * 60 * 1000)
        // convert to Eastern Time
        let sampleDeadlineEST = dayjs(threeHoursFromNow).tz("America/New_York").format('hh:mm A')
        let sampleDeadlineDateEST = dayjs(threeHoursFromNow).tz("America/New_York").format('MM/DD/YYYY [at] hh:mm A')

        return {
            sampleTestDeadline: sampleDeadlineEST,
            sampleTestDeadlineDate: sampleDeadlineDateEST,
            sampleTestDeadlineTimezoneAbbreviation: getEasternTimeZoneAbbreviation(threeHoursFromNow)
        }
    }

    setSampleTest = (callback) => {
        // set originalAccessCode to accessCode
        // prepend "LFSAMPLE" to access code
        // put user into normal workflow
        const realAccessCode = this.getRealAccessCode();
        console.log('Setting Sample Test...');
        this.setState({
            originalAccessCode: realAccessCode,
            accessCode: this.getSampleAccessCode(realAccessCode),
            isSampleTest: true
        }, callback)
    }

    beginSampleTest = (nextStep, callback) => {
        const realAccessCode = this.getRealAccessCode();
        const deadlineState = this.getSampleDeadlineState();

        console.log('Beginning Sample Test...');
        this.setState({
            originalAccessCode: realAccessCode,
            accessCode: this.getSampleAccessCode(realAccessCode),
            isSampleTest: true,
            currentStep: nextStep,
            ...deadlineState
        }, callback)
    }

    setSampleTestDeadline = () => {
        const deadlineState = this.getSampleDeadlineState();
        // set in state
        console.log('Setting Sample Test Deadline to ', deadlineState.sampleTestDeadline)
        this.setState(deadlineState)
    }

    removeSampleTestDeadline = () => {
        console.log('Clearing sampleTestDeadline...');
        this.setState({
            sampleTestDeadline: '',
            sampleTestDeadlineDate: '',
            sampleTestDeadlineTimezoneAbbreviation: ''
        })
    }

    exitSampleTest = () => {
        // Business rule: User is only allowed to exit a sample test when sample test is completed
        const realAccessCode = this.getRealAccessCode();
        console.log('Exiting Sample Test...');
        this.setState({
            accessCode: realAccessCode,
            originalAccessCode: '',
            isSampleTest: false,
            currentStep: ''
        })
    }

    setLeapFrogTest = () => {
        //console.log('...LeapFrog Test Detected');
        this.setState({
            isLeapFrogTest: true
        })
    }

    //  TODO: check to see if this can be removed...
    showTheTimeOutModal = () => {
        console.log('Test has timed out, showing the timeout message...');
        this.setState({
            showTimeOutModal: true
            // trigger log out here?
        }
        // , () => {this.logoutUser()}
        )
    }

    setTestTimedOut = () => {
        console.log('timed out, user should be redirected to dashboard and shown the timeout card');
        // if the user times out, set the current step to '/viewresults', stepnumber to 7, set testUnderway to false
        if (this.state.currentTestStepNumber === 5) { // clear localStorage in case test is reset
            window.localStorage.clear()
        }
        this.setState({
            testTimedOut: true,
            currentStep: '/viewresults',
            currentTestStepNumber: 7,
            testUnderway: false
        })
    }


    // TODO: refactor this context name, because it sets so much more now
    setContactAndFacilityName = (d) => {
        //console.log('test role ... ' + d['tt-role']);

        this.setState({
            testContact: d['hospital-contact-name'],
            testFacilityName: d['hospital-name'],
            testType: d['tt-type'], // the old d['test-type'] is currently used to set 'isLeapfrogTest' and is DEPRECATED
            leapFrogHospitalId: d['leapfrog-hospital-id'],//TODO: Should be renamed to Medicare Facility ID. This is NOT the Leapfrog Unique ID used internally by LF code.
            ttGroup: d['tt-group'],
            ttYear: d['tt-year'],
            ttRole: d['tt-role'],
            // ehr-vendor: "1" => VistA/CPRS (Legacy), "2" => Federal EHR (Cerner)
            ehrVendor: d['ehr-vendor'],
            // Propagate global timeout configuration
            allTimeoutsAllowed: d['all-timeouts-allowed']
        })
    }

    async componentDidMount() {
        // setting state in context of configuration received from the server
        fetch(`/config`)
        .then(response => response.json())
        .then(data => {
            const decodedMotd = decodeURIComponent(data.ednMotd);
            console.log("decodedMotd ... "+decodedMotd);
            this.setState({
            clientUrlRoot: data.ednClientUrlRoot,
            apiUrlRoot: data.ednApiUrlRoot,
            leapFrogDashboardUrl: data.ednLeapfrogDashboard,
            serverDisplayName: data.ednServerType,
            motd: decodedMotd,
        },
        function() {
            console.log("site client server config for clientUrlRoot:" + this.state.clientUrlRoot + "; apiUrlRoot:" + this.state.apiUrlRoot + "; serverDisplayName:" + this.state.serverDisplayName + "; motd:" + this.state.motd)
            console.log("server config LF Dashboard Url: ", this.state.leapFrogDashboardUrl);
        })});
    }


    render() {
        const { children } = this.props;
        return (
          <UserContext.Provider
            value={{
                // methods and state
                loginUser: this.loginUser,
                logoutUser: this.logoutUser,
                switchTest: this.switchTest, // Provide the switchTest method
                setCurrentStep: this.setCurrentStep,
                setStepNumber: this.setStepNumber,
                getApiConfig: this.getApiConfig,
                getEffectiveAccessCode: this.getEffectiveAccessCode,
                setSampleTest: this.setSampleTest,
                beginSampleTest: this.beginSampleTest,
                exitSampleTest: this.exitSampleTest,
                setLeapFrogTest: this.setLeapFrogTest,
                showTheTimeOutModal: this.showTheTimeOutModal,
                setTestTimedOut: this.setTestTimedOut,
                setContactAndFacilityName: this.setContactAndFacilityName,
                setSampleTestDeadline: this.setSampleTestDeadline,
                removeSampleTestDeadline: this.removeSampleTestDeadline,
                // timerStart: this.timerStart,
                // updateTimeLeft: this.updateTimeLeft,
                // setUserInContext: this.setUserInContext,
                isLoggedIn: this.state.isLoggedIn,
                isLeapFrogTest: this.state.isLeapFrogTest,
                isSampleTest: this.state.isSampleTest,
                currentStep: this.state.currentStep,
                testUnderway: this.state.testUnderway,
                currentTestStepNumber: this.state.currentTestStepNumber,
                accessCode: this.state.accessCode,
                userEmail: this.state.userEmail,
                clientUrlRoot: this.state.clientUrlRoot,
                apiUrlRoot: this.state.apiUrlRoot,
                serverDisplayName: this.state.serverDisplayName,
                motd: this.state.motd,
                leapFrogDashboardUrl: this.state.leapFrogDashboardUrl,
                testTimer: this.state.testTimer,
                timeLeft: this.timeLeft,
                sampleTestDeadline: this.state.sampleTestDeadline,
                sampleTestDeadlineDate: this.state.sampleTestDeadlineDate,
                sampleTestDeadlineTimezoneAbbreviation: this.state.sampleTestDeadlineTimezoneAbbreviation,
                deadline: this.state.deadline,
                deadlineDate: this.state.deadlineDate,
                deadlineTimezoneAbbreviation: this.state.deadlineTimezoneAbbreviation,
                showTimeOutModal: this.state.showTimeOutModal,
                testTimedOut: this.state.testTimedOut,
                testContact: this.state.testContact,
                testFacilityName: this.state.testFacilityName,
                testType: this.state.testType,
                leapFrogHospitalId: this.state.leapFrogHospitalId,
                ttGroup: this.state.ttGroup,
                ttYear: this.state.ttYear,
                ttRole: this.state.ttRole,
                ehrVendor: this.state.ehrVendor,
                // Global configuration passed from server
                allTimeoutsAllowed: this.state.allTimeoutsAllowed
            }}
          >
            {children}
          </UserContext.Provider>
        );
      }
}

export const UserContextConsumer = UserContext.Consumer;
export default UserContextProvider;
